import { useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  getWhatsAppUpdates,
  whatsappKeys,
  type PersonThreads,
  type ThreadDetail,
  type ThreadListParams,
  type ThreadSummary,
  type WhatsAppUnread,
  type WhatsAppUpdates,
} from "@/api/whatsapp";
import type { MessagesData, ThreadListData } from "./use-whatsapp";

/*
 * WhatsApp's one polling loop.
 *
 * The user asked for the inbox to stay live without loading the server
 * (2026-10-23), so there is exactly one request per tick for the whole app —
 * however many lists, panels and badges are on screen — and it only asks what
 * changed since the last answer (`since` is the server's own clock). The
 * answer is written into the TanStack caches the screens already read.
 *
 * How often, by what is on screen:
 *   5 s   a thread is open and something changed in the last 2 minutes
 *   15 s  the inbox page or the docked panel is open
 *   30 s  only the top-bar badge
 *   —     the tab is hidden (one tick the moment it is shown again)
 */

const FAST_MS = 5_000;
const OPEN_MS = 15_000;
const BADGE_MS = 30_000;
const RECENT_CHANGE_MS = 120_000;
/** bella lists at most this many changed threads per answer. */
const UPDATES_THREAD_CAP = 100;

type Surface = "page" | "dock";

const surfaces = new Map<symbol, Surface>();
const openThreads = new Map<symbol, { threadId: number; focusedAt: number }>();
const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of listeners) listener();
}

/** Marks the inbox page or the docked panel as on screen, while mounted. */
export function useWhatsAppSurface(kind: Surface): void {
  useEffect(() => {
    const key = Symbol(kind);
    surfaces.set(key, kind);
    changed();
    return () => {
      surfaces.delete(key);
      changed();
    };
  }, [kind]);
}

/**
 * Registers the thread a view shows. When the page and the panel both show
 * one, the one used last is the one polled; call the returned `focus` on any
 * interaction with the view.
 */
export function useActiveThread(threadId: number | null): () => void {
  const key = useRef(Symbol("thread"));
  useEffect(() => {
    const own = key.current;
    if (threadId === null) return;
    openThreads.set(own, { threadId, focusedAt: Date.now() });
    changed();
    return () => {
      openThreads.delete(own);
      changed();
    };
  }, [threadId]);

  return () => {
    const entry = openThreads.get(key.current);
    if (entry && Date.now() - entry.focusedAt > 1_000) {
      entry.focusedAt = Date.now();
      changed();
    }
  };
}

function activeThreadId(): number | null {
  let best: { threadId: number; focusedAt: number } | null = null;
  for (const entry of openThreads.values()) {
    if (!best || entry.focusedAt > best.focusedAt) best = entry;
  }
  return best?.threadId ?? null;
}

/** The newest message id the open thread already holds. */
function lastMessageId(queryClient: QueryClient, threadId: number): number | null {
  const data = queryClient.getQueryData<MessagesData>(whatsappKeys.messages(threadId));
  const newest = data?.pages[0]?.items;
  if (!newest?.length) return null;
  return Math.max(...newest.map((message) => message.id));
}

/**
 * Runs the loop. Mounted once, in the app shell, and only for admins with
 * `whatsapp.view`.
 */
export function useWhatsAppLive(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    let since: number | null = null;
    let lastChangeAt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight: AbortController | null = null;
    let dueAt = 0;
    let stopped = false;
    // `now` comes back 5 s early (clock-skew overlap), so the same thread or
    // message can arrive twice. Merging twice is harmless; counting it as
    // news twice would keep the fast cadence going for nothing.
    const seen = new Map<string, string>();
    const isNews = (updates: WhatsAppUpdates): boolean => {
      let news = false;
      const note = (key: string, signature: string) => {
        if (seen.get(key) !== signature) news = true;
        seen.set(key, signature);
      };
      for (const t of updates.threads) note(`t${t.id}`, String(t.updatedAt));
      for (const m of updates.messages) note(`m${m.id}`, m.status ?? "");
      if (seen.size > 2_000) seen.clear();
      return news;
    };

    const delay = (): number => {
      const threadOpen = activeThreadId() !== null;
      if (threadOpen && Date.now() - lastChangeAt < RECENT_CHANGE_MS) return FAST_MS;
      if (surfaces.size > 0 || threadOpen) return OPEN_MS;
      return BADGE_MS;
    };

    const schedule = (ms: number) => {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      dueAt = Date.now() + ms;
      timer = setTimeout(() => void tick(), ms);
    };

    const tick = async () => {
      timer = null;
      if (stopped) return;
      // A hidden tab asks nothing; showing it again ticks at once.
      if (document.hidden) return;
      if (inFlight) return;
      const threadId = activeThreadId();
      const controller = new AbortController();
      inFlight = controller;
      try {
        const updates = await getWhatsAppUpdates(
          {
            since,
            threadId,
            afterMessageId:
              threadId === null ? null : lastMessageId(queryClient, threadId),
          },
          controller.signal,
        );
        since = updates.now;
        mergeUpdates(queryClient, updates, threadId);
        if (isNews(updates)) lastChangeAt = Date.now();
      } catch {
        // A failed tick is retried by the next one; the screens show their own
        // errors from their own queries.
      } finally {
        inFlight = null;
      }
      schedule(delay());
    };

    // Something opened or closed: tick sooner if the new cadence is faster.
    const onChange = () => {
      const wanted = delay();
      if (timer && dueAt - Date.now() > wanted) schedule(Math.min(wanted, 1_000));
    };
    const onVisible = () => {
      if (!document.hidden && !timer && !inFlight) void tick();
    };

    listeners.add(onChange);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    void tick();

    return () => {
      stopped = true;
      listeners.delete(onChange);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (timer) clearTimeout(timer);
      inFlight?.abort();
    };
  }, [enabled, queryClient]);
}

/** Does a thread belong in a list with these filters? Search is the server's. */
function belongs(params: ThreadListParams, thread: ThreadSummary): boolean {
  if (params.numberId !== null && thread.number.id !== params.numberId) return false;
  switch (params.filter) {
    case "unread":
      return thread.unreadCount > 0;
    case "needs_reply":
      return thread.lastMessage.direction === "in";
    case "ai":
      return thread.handledBy === "ai";
    case "human":
      return thread.handledBy === "human";
    default:
      return true;
  }
}

const newestFirst = (a: ThreadSummary, b: ThreadSummary) =>
  (b.lastMessage.at ?? 0) - (a.lastMessage.at ?? 0) || b.id - a.id;

/**
 * Writes one answer into the caches. Idempotent — every write replaces by
 * id — because consecutive answers overlap by a few seconds.
 *
 * - Lists: a changed thread leaves wherever it was and goes back in at its
 *   place by last message — if it still matches the list's filters. A searched
 *   list is refetched instead (the search is the server's), and so is every
 *   list when the answer is capped at 100 threads (more changed than it says).
 * - A thread's detail and a person's tabs take the new summary; a new thread
 *   of that person joins their tabs.
 * - The open thread gets its new messages and status changes.
 */
export function mergeUpdates(
  queryClient: QueryClient,
  updates: WhatsAppUpdates,
  threadId: number | null,
): void {
  queryClient.setQueryData<WhatsAppUnread>(whatsappKeys.unread(), updates.unread);

  if (updates.threads.length >= UPDATES_THREAD_CAP) {
    // Capped answer: more changed than it lists. Ask again rather than patch.
    void queryClient.invalidateQueries({ queryKey: whatsappKeys.threadLists() });
    void queryClient.invalidateQueries({ queryKey: whatsappKeys.people() });
    for (const thread of updates.threads) {
      queryClient.setQueryData<ThreadDetail>(
        whatsappKeys.thread(thread.id),
        (detail) => detail && { ...detail, ...thread },
      );
    }
  } else if (updates.threads.length) {
    for (const [key, data] of queryClient.getQueriesData<ThreadListData>({
      queryKey: whatsappKeys.threadLists(),
    })) {
      if (!data) continue;
      const params = key[2] as ThreadListParams | undefined;
      if (!params) continue;
      if (params.search) {
        void queryClient.invalidateQueries({ queryKey: key, exact: true });
        continue;
      }
      const ids = new Set(updates.threads.map((t) => t.id));
      const pages = data.pages.map((page) => ({
        ...page,
        items: page.items.filter((t) => !ids.has(t.id)),
      }));
      const incoming = updates.threads.filter((t) => belongs(params, t));
      if (pages[0]) {
        pages[0] = {
          ...pages[0],
          items: [...incoming, ...pages[0].items].sort(newestFirst),
        };
      }
      queryClient.setQueryData<ThreadListData>(key, { ...data, pages });
    }

    for (const thread of updates.threads) {
      queryClient.setQueryData<ThreadDetail>(
        whatsappKeys.thread(thread.id),
        (detail) => detail && { ...detail, ...thread },
      );
    }

    for (const [key, data] of queryClient.getQueriesData<PersonThreads>({
      queryKey: whatsappKeys.people(),
    })) {
      if (!data) continue;
      const { type, id } = data.person;
      let threads = data.threads.map(
        (t) => updates.threads.find((u) => u.id === t.id) ?? t,
      );
      const added = updates.threads.filter(
        (u) =>
          !threads.some((t) => t.id === u.id) &&
          u.people.some((p) => p.type === type && p.id === id),
      );
      if (added.length) threads = [...threads, ...added];
      queryClient.setQueryData<PersonThreads>(key, { ...data, threads });
    }
  }

  if (threadId !== null && (updates.messages.length || updates.sessions.length)) {
    queryClient.setQueryData<MessagesData>(whatsappKeys.messages(threadId), (data) => {
      if (!data || !data.pages[0]) return data;
      const byId = new Map(updates.messages.map((m) => [m.id, m]));
      const pages = data.pages.map((page) => ({
        ...page,
        items: page.items.map((m) => {
          const update = byId.get(m.id);
          if (update) byId.delete(m.id);
          return update ?? m;
        }),
      }));
      const first = pages[0]!;
      const sessions = [
        ...first.sessions.filter((s) => !updates.sessions.some((u) => u.id === s.id)),
        ...updates.sessions,
      ];
      pages[0] = {
        ...first,
        sessions,
        items: [...first.items, ...byId.values()].sort(
          (a, b) => a.sentAt - b.sentAt || a.id - b.id,
        ),
      };
      return { ...data, pages };
    });
  }
}
